<template>
  <b-modal
    :title="$t('translationManager.openDialog.title')"
    body-class="open-translation-project-modal"
    size="xl"
    v-model="localShow"
    header-close-content=""
    @ok="onConfirm"
    @cancel="onCancel">
    <div class="open-translation-project-modal-filter-container">
      <CommonFilter v-model="vm.searchTerm" :placeholder="$t('FilterByName')" triggerByTime />
    </div>

    <div class="translation-project-table-container">
      <table class="table translation-project-table">
        <thead>
          <tr>
            <th>{{ $t("name") }}</th>
            <th>{{ $t("Catalogue") }}</th>
            <th>{{ $t("translationManager.sourceLanguage") }}</th>
            <th>{{ $t("translationManager.targetLanguage") }}</th>
            <th>{{ $t("UpdatedOn") }}</th>
            <th>{{ $t("UpdatedBy") }}</th>
            <th>{{ $t("CreatedOn") }}</th>
            <th>{{ $t("CreatedBy") }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="project in sortedTranslationProjects" :key="project.translationProjectId">
            <tr
              @click="selectTranslationProject(project)"
              :class="{ 'table-active': vm.selectedTranslationProject?.translationProjectId == project.translationProjectId }">
              <td>{{ project.name }}</td>
              <td>{{ project.catalog.name }}</td>
              <td>{{ getLanguageDisplayName(project.sourceLanguage?.languageCode) }}</td>
              <td>{{ getLanguageDisplayName(project.targetLanguage?.languageCode) }}</td>
              <td>{{ project.updatedOn ? $d(project.updatedOn, "shortDateTime") : "" }}</td>
              <td>{{ project.updatedBy }}</td>
              <td>{{ project.createdOn ? $d(project.createdOn, "shortDateTime") : "" }}</td>
              <td>{{ project.createdBy }}</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <template #modal-footer="{ ok, cancel }">
      <b-btn variant="primary" :disabled="!vm.selectedTranslationProject" @click="ok">{{ $t("Open") }}</b-btn>
      <b-btn @click="cancel">{{ $t("close") }}</b-btn>
    </template>
  </b-modal>
</template>

<script setup lang="ts">
import CommonFilter from "@common/components/CommonFilter.vue";
import { TranslationProject } from "@medapro-types/TranslationManager/translationManager";
import { getLanguageDisplayName } from "@util/languages";
import { naturalSort } from "@util/naturalSort";
import { computed, DeepReadonly, reactive } from "vue";

//#region General
const props = withDefaults(
  defineProps<{
    show: boolean;
    translationProjects: DeepReadonly<TranslationProject[]>;
  }>(),
  {}
);
const emits = defineEmits(["update:show", "confirm", "cancel"]);

//#endregion

//#region ViewModel
type ViewModel = {
  searchTerm: string | null;
  selectedTranslationProject: DeepReadonly<TranslationProject> | null;
};

const vm = reactive<ViewModel>({
  searchTerm: null,
  selectedTranslationProject: null
});
//#endregion

//#region Data
const sortedTranslationProjects = computed(() => {
  const sorted = naturalSort(props.translationProjects, "name");
  if (vm.searchTerm) {
    return sorted.filter((project) => project.name.includes(vm.searchTerm!));
  }

  return sorted;
});

const selectTranslationProject = (project: DeepReadonly<TranslationProject> | null) => {
  vm.selectedTranslationProject = project;
};

const onConfirm = () => {
  if (vm.selectedTranslationProject) {
    emits("confirm", vm.selectedTranslationProject);
  }
};

const onCancel = () => {
  emits("cancel");
};
//#endregion

//#region Show
const localShow = computed<boolean>({
  get: () => props.show,
  set: (nv) => emits("update:show", nv)
});
//#endregion
</script>
